import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaCalendarCheck,
  FaChalkboard,
  FaClock,
  FaComments,
  FaHeadset,
  FaHotel,
  FaPaperPlane,
  FaStar,
  FaTimes,
} from 'react-icons/fa';
import { sendAiChatMessage } from '../../services/aiChatService';
import { getAiChatFallbackReply } from '../../utils/aiChatFallback';
import './HomeAiChatPanel.css';

const WELCOME_ID = 'welcome';

const BOTTOM_NAV = [
  { label: 'Best Hotels', icon: FaHotel, path: '/search' },
  { label: 'Meeting Rooms', icon: FaChalkboard, path: '/meeting-home' },
  { label: 'Easy Bookings', icon: FaCalendarCheck, path: '/trips' },
  { label: '24/7 Support', icon: FaHeadset, path: '/help' },
];

const makeWelcome = (name) => ({
  id: WELCOME_ID,
  role: 'assistant',
  content: name
    ? `Hello, ${name}! How can I help you with hotels, meeting rooms, and your bookings today?`
    : 'Hello! How can I help you with hotels, meeting rooms, and your bookings today?',
  timeLabel: 'Just now',
});

const HeaderTravelArt = () => (
  <svg
    className="ubsa-chat-header-art"
    viewBox="0 0 200 90"
    aria-hidden
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 28 Q40 8 72 22 T120 18"
      fill="none"
      stroke="rgba(255,255,255,0.55)"
      strokeWidth="1.5"
      strokeDasharray="4 5"
    />
    <path d="M118 16 L128 14 L124 20 Z" fill="rgba(255,255,255,0.9)" />
    <rect x="118" y="38" width="34" height="26" rx="4" fill="#f8bbd0" stroke="#fff" strokeWidth="1.2" />
    <rect x="122" y="42" width="26" height="8" rx="2" fill="#fff" opacity="0.7" />
    <rect x="128" y="52" width="6" height="12" fill="#ad1457" />
    <circle cx="152" cy="48" r="10" fill="#ffc107" stroke="#fff" strokeWidth="1.2" />
    <ellipse cx="152" cy="48" rx="8" ry="3" fill="#ff8f00" opacity="0.5" />
    <rect x="158" y="28" width="38" height="42" rx="3" fill="#fce4ec" stroke="#fff" strokeWidth="1.2" />
    <rect x="164" y="34" width="8" height="8" fill="#f48fb1" />
    <rect x="176" y="34" width="8" height="8" fill="#f48fb1" />
    <rect x="164" y="46" width="8" height="8" fill="#f48fb1" />
    <rect x="176" y="46" width="8" height="8" fill="#f48fb1" />
    <rect x="170" y="22" width="18" height="8" rx="2" fill="#c2185b" />
    <text x="171" y="28" fontSize="5" fill="#fff" fontWeight="700">
      HOTEL
    </text>
    <polygon points="182,62 192,58 188,68" fill="#fff" opacity="0.9" />
    <text x="183" y="66" fontSize="4" fill="#c2185b" fontWeight="700">
      BOOK
    </text>
    <ellipse cx="108" cy="72" rx="6" ry="10" fill="#81c784" opacity="0.9" />
    <ellipse cx="100" cy="68" rx="5" ry="9" fill="#66bb6a" opacity="0.85" />
  </svg>
);

const renderText = (text, navigate) =>
  String(text)
    .split(/(\/[a-z0-9\-/_]+|\*\*[^*]+\*\*)/gi)
    .map((part, i) => {
      if (/^\/[a-z0-9\-/_]+$/i.test(part)) {
        return (
          <button key={i} type="button" className="ubsa-chat-link" onClick={() => navigate(part)}>
            {part}
          </button>
        );
      }
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return <span key={i}>{part}</span>;
    });

const formatTimeLabel = (ts) => {
  if (!ts) return 'Just now';
  const diff = Date.now() - ts;
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const HomeAiChatPanel = ({ userName: userNameProp }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => [makeWelcome(userNameProp)]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    setMessages([makeWelcome(userNameProp)]);
  }, [userNameProp]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, loading, open]);

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = String(text || '').trim();
      if (!trimmed || loading) return;

      const now = Date.now();
      const userMsg = { id: `u-${now}`, role: 'user', content: trimmed, ts: now };
      const next = [...messages.filter((m) => m.id !== WELCOME_ID), userMsg];
      setMessages(next);
      setInput('');
      setLoading(true);

      try {
        const { reply } = await sendAiChatMessage({
          messages: next.map((m) => ({ role: m.role, content: m.content })),
          userName: userNameProp,
          pagePath: '/',
        });
        const replyTs = Date.now();
        setMessages((prev) => [
          ...prev,
          {
            id: `a-${replyTs}`,
            role: 'assistant',
            content: reply || 'How can I help you today?',
            ts: replyTs,
            timeLabel: 'Just now',
          },
        ]);
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            id: `e-${Date.now()}`,
            role: 'assistant',
            content: getAiChatFallbackReply(trimmed),
            ts: Date.now(),
            timeLabel: 'Just now',
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [loading, messages, userNameProp]
  );

  return (
    <div className="ubsa-chat-widget">
      {open && (
        <div className="ubsa-chat-window" role="dialog" aria-label="UBSAAPP Assistant">
          <header className="ubsa-chat-header">
            <div className="ubsa-chat-header-inner">
              <div className="ubsa-chat-header-left">
                <span className="ubsa-chat-header-icon" aria-hidden>
                  <FaComments />
                </span>
                <div>
                  <h2 className="ubsa-chat-title">UBSAAPP Assistant</h2>
                  <p className="ubsa-chat-subtitle">Ask me about bookings &amp; travel</p>
                </div>
              </div>
              <button
                type="button"
                className="ubsa-chat-close"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
              >
                <FaTimes />
              </button>
            </div>
            <HeaderTravelArt />
            <svg
              className="ubsa-chat-header-wave"
              viewBox="0 0 400 24"
              preserveAspectRatio="none"
              aria-hidden
            >
              <path
                d="M0,24 C80,4 160,20 240,10 C320,0 360,18 400,8 L400,24 L0,24 Z"
                fill="#ffffff"
              />
            </svg>
          </header>

          <div className="ubsa-chat-body" ref={bodyRef}>
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`ubsa-chat-row ubsa-chat-row--${msg.role}`}
              >
                {msg.role === 'assistant' && (
                  <span className="ubsa-chat-avatar" aria-hidden>
                    <FaStar />
                  </span>
                )}
                <div className="ubsa-chat-bubble-block">
                  <div
                    className={`ubsa-chat-message ubsa-chat-message--${msg.role}${
                      msg.id === WELCOME_ID ? ' ubsa-chat-message--welcome' : ''
                    }`}
                  >
                    <p>{renderText(msg.content, navigate)}</p>
                  </div>
                  {msg.role === 'assistant' && (
                    <span className="ubsa-chat-time">
                      <FaClock aria-hidden />
                      {msg.timeLabel || formatTimeLabel(msg.ts)}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="ubsa-chat-row ubsa-chat-row--assistant">
                <span className="ubsa-chat-avatar" aria-hidden>
                  <FaStar />
                </span>
                <div className="ubsa-chat-message ubsa-chat-message--assistant ubsa-chat-message--typing">
                  <p>
                    <span className="ubsa-chat-dot" />
                    <span className="ubsa-chat-dot" />
                    <span className="ubsa-chat-dot" />
                  </p>
                </div>
              </div>
            )}
          </div>

          <form
            className="ubsa-chat-input-bar"
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage(input);
            }}
          >
            <button
              type="button"
              className="ubsa-chat-input-icon"
              onClick={() => navigate('/search')}
              aria-label="Search hotels"
            >
              <FaHotel />
            </button>
            <div className="ubsa-chat-input-wrap">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your question..."
                disabled={loading}
                maxLength={500}
                aria-label="Type your question"
              />
            </div>
            <button
              type="submit"
              className="ubsa-chat-send"
              disabled={loading || !input.trim()}
              aria-label="Send message"
            >
              <FaPaperPlane />
            </button>
            <button
              type="button"
              className="ubsa-chat-input-icon"
              onClick={() => navigate('/trips')}
              aria-label="View bookings"
            >
              <FaCalendarCheck />
            </button>
          </form>

          <nav className="ubsa-chat-bottom-nav" aria-label="Quick links">
            {BOTTOM_NAV.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.label}
                  type="button"
                  className="ubsa-chat-nav-item"
                  onClick={() => navigate(item.path)}
                >
                  <Icon aria-hidden />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      )}

      {!open && (
        <button
          type="button"
          className="ubsa-chat-launcher"
          onClick={() => setOpen(true)}
          aria-label="Open UBSAAPP Assistant"
        >
          <FaComments />
        </button>
      )}
    </div>
  );
};

export default HomeAiChatPanel;
